import {
  UseUserIdentityValidateByOriginIdProps,
  UseUserIdentityValidateByOriginIdReturnType,
} from './props';
import { useUserIdentitiesValidate, useUserIdentityLookUp } from '../../../api';
import { KycIdentityOriginId } from '../../../enums';

/**
 * Custom hook to validate user identity based on the origin ID.
 *
 * @template T - The type of the origin ID.
 * @param {UseUserIdentityValidateByOriginIdProps<T>} props - The properties for the hook.
 * @param {T} props.originId - The origin ID for the user identity.
 * @param {object} props.payload - The payload containing user identity details.
 * @param {string} [props.payload.identifier] - The identifier for the user.
 * @param {string} [props.payload.birthDate] - The birth date of the user.
 * @param {string} [props.payload.name] - The name of the user.
 * @param {boolean} [props.enabled=true] - Flag to enable or disable the hook.
 *
 * @returns {UseUserIdentityValidateByOriginIdReturnType<T>} - Returns the query result based on the origin ID.
 *
 * Example usage:
 * const result = useUserIdentityValidateByOriginId({
 *   originId: 'Brazil',
 *   payload: { identifier: '12345', birthDate: '1990-01-01', name: 'John Doe' },
 *   enabled: true,
 * });
 */
export function useUserIdentityValidateByOriginId<T extends string>({
  originId,
  payload,
  enabled = true,
}: UseUserIdentityValidateByOriginIdProps<T>): UseUserIdentityValidateByOriginIdReturnType<T> {
  // Destructure the payload to extract identifier, birthDate, and name
  const { identifier, birthDate, name } = payload ?? {};

  // Hook to look up user identity based on origin ID and identifier
  const userIdentityLookUpQuery = useUserIdentityLookUp({
    originId,
    identifier,
    enabled:
      originId?.toString() == KycIdentityOriginId.Brazil?.toString() && enabled,
  });

  // Hook to validate user identity based on identifier, origin ID, birthDate, and name
  const userIdentityValidateQuery = useUserIdentitiesValidate({
    identifier,
    originId: originId?.toString(),
    birthDate,
    name,
    enabled:
      originId?.toString() != KycIdentityOriginId.Brazil?.toString() && enabled,
  });

  // Determine which query result to return based on the origin ID
  switch (originId?.toString()) {
    case KycIdentityOriginId.Brazil?.toString():
      return userIdentityLookUpQuery as UseUserIdentityValidateByOriginIdReturnType<T>;
    case KycIdentityOriginId.India?.toString():
    case KycIdentityOriginId.Argentina?.toString():
    case KycIdentityOriginId.Mexico?.toString():
    case KycIdentityOriginId.Indonesia?.toString():
    case KycIdentityOriginId.Peru?.toString():
    case KycIdentityOriginId.SouthAfrica?.toString():
    case KycIdentityOriginId.Singapore?.toString():
    case KycIdentityOriginId.Nigeria?.toString():
      return userIdentityValidateQuery as UseUserIdentityValidateByOriginIdReturnType<T>;
    default:
      return userIdentityLookUpQuery as UseUserIdentityValidateByOriginIdReturnType<T>;
  }
}

export type {
  UseUserIdentityValidateByOriginIdProps,
  UseUserIdentityValidateByOriginIdReturnType,
};
